// Great Lakes Trolling: a full-screen map of NOAA Great Lakes conditions.
import "leaflet/dist/leaflet.css";
import "../css/app.css";
import { html, setHtml } from "./html.js";
import { L } from "./vendor.js";
import { ui } from "./state.js";
import { setup as setupApi } from "./api.js";
import {
  GREAT_LAKES_MODEL_BOUNDS, LAYER_OPTIONS, ensureGreatLakesConditions, friendlyTime, greatLakesDepthFromSlider, greatLakesDepthLabel, greatLakesHistoryTime,
  loadGreatLakesConditions, setGreatLakesHistoryTime, setMapLoadingReason, setup as setupConditions, syncGreatLakesLayerControls, targetTemperatureC, setTargetTemperature
} from "./great-lakes-conditions.js";
import { parseViewHash, sharedViewHash, sliderFromDepth, viewHash } from "./link.js";
import { setup as setupPoints } from "./great-lakes-points.js";
import { currentUnitSystem, formatUnitValue, setUnitSystem, unitPreference } from "./units.js";
import { addMeasureControl, isMeasuring } from "./measure.js";
import { positionLabel } from "./cards.js";
import { pauseGreatLakesAnimation } from "./great-lakes-animation.js";
import { setTemperatureColorMode } from "./great-lakes-palette.js";
import { setupPortVerification, isPortVerificationEditing } from "./port-verification.js";
import { setupAnalytics } from "./analytics.js";
import { setupMapAnalytics } from "./map-analytics.js";

setupAnalytics();

// Renamed when the dark map became the default: older pages saved "standard" on every visit.
export const BASEMAP_STORAGE_KEY = "glc.MapStyle";
export const DEFAULT_BASEMAP = "dark";
export const PANEL_STORAGE_KEY = "glc.PanelOpen";
export const MAP_HINTS_STORAGE_KEY = "glc.MapHintsSeen";
const MAP_PROFILE_HINT_SEEN_KEY = "glc.MapProfileHintSeen";
export const ALL_LAKES_BOUNDS = [[41.2, -92.4], [49.1, -75.9]];
const MAP_TUTORIAL_CENTER = [43.3219, -79.1006];
const MAP_TUTORIAL_ZOOM = 12;
const MAP_TUTORIAL_SPOTLIGHT_CENTER = [43.3197, -79.1380];
const MAP_TUTORIAL_SPOTLIGHT_ZOOM = 12;
const MAP_TUTORIAL_SPOTLIGHT_WIDTH = 144;
const MAP_TUTORIAL_SPOTLIGHT_HEIGHT = 108;
const MAP_TUTORIAL_ALLOWED_LAYERS = new Set(["temperature", "currents", "thermocline"]);
const ESRI_DARK_ATTRIBUTION = "Tiles &copy; Esri, HERE, Garmin, &copy; OpenStreetMap contributors, and the GIS User Community";
export const BASEMAPS = {
  dark: {
    label: "Dark",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}",
    // Place names go above the data layers so they stay readable over the lakes.
    labelsUrl: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}",
    options: { attribution: ESRI_DARK_ATTRIBUTION, maxNativeZoom: 16, maxZoom: 19 }
  },
  standard: {
    label: "Standard",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    options: { attribution: "&copy; OpenStreetMap contributors", maxZoom: 19 }
  },
  satellite: {
    label: "Satellite",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    options: { attribution: "Imagery &copy; Esri, Maxar, Earthstar Geographics, and the GIS User Community", maxZoom: 19 }
  }
};

let basemapLayer = null;
let labelsLayer = null;
// A slow tile server cannot hold the loading card up for longer than this.
const BASEMAP_LOADING_LIMIT_MS = 15000;

// The Dark map uses CARTO when the server has a CARTO key (sent in the page);
// labels are a separate layer so place names stay readable over the data.
export function basemapConfig(name, cartoKey = document.body?.dataset.cartoKey || "") {
  if (name !== "dark" || !cartoKey) return BASEMAPS[name];
  const key = encodeURIComponent(cartoKey);
  return {
    label: "Dark",
    url: `https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}{r}.png?key=${key}`,
    labelsUrl: `https://{s}.basemaps.cartocdn.com/dark_only_labels/{z}/{x}/{y}{r}.png?key=${key}`,
    options: { attribution: "&copy; OpenStreetMap contributors &copy; CARTO", subdomains: "abcd", maxZoom: 20 },
    labelOptions: { subdomains: "abcd", maxZoom: 20 }
  };
}

export function savedBasemap() {
  try {
    const saved = localStorage.getItem(BASEMAP_STORAGE_KEY);
    return BASEMAPS[saved] ? saved : DEFAULT_BASEMAP;
  } catch { return DEFAULT_BASEMAP; }
}

export function setBasemap(map, key) {
  const name = BASEMAPS[key] ? key : DEFAULT_BASEMAP;
  const config = basemapConfig(name);
  basemapLayer?.remove();
  labelsLayer?.remove();
  labelsLayer = null;
  basemapLayer = L.tileLayer(config.url, config.options).addTo(map);
  basemapLayer.bringToBack();
  basemapLayer.once("tileerror", () => {
    document.dispatchEvent(new CustomEvent("great-lakes-client-error", {
      detail: { errorType: "MapTileError", source: `basemap:${name}` }
    }));
  });
  // The loading card stays up until the new map's first tiles are in (or a slow
  // connection has had a fair chance); later tiles from panning load quietly.
  setMapLoadingReason("basemap", true, "Loading the map…");
  const layer = basemapLayer;
  const tilesIn = () => { if (basemapLayer === layer) setMapLoadingReason("basemap", false); };
  basemapLayer.once("load", tilesIn);
  setTimeout(tilesIn, BASEMAP_LOADING_LIMIT_MS);
  if (config.labelsUrl) {
    if (!map.getPane("basemapLabels")) {
      // Above the data layers (overlay pane, 400), below model points and stations.
      const pane = map.createPane("basemapLabels");
      pane.style.zIndex = 440;
      pane.style.pointerEvents = "none";
    }
    labelsLayer = L.tileLayer(config.labelsUrl, { pane: "basemapLabels", ...(config.labelOptions || { maxNativeZoom: 16, maxZoom: 19 }) }).addTo(map);
    labelsLayer.once("tileerror", () => {
      document.dispatchEvent(new CustomEvent("great-lakes-client-error", {
        detail: { errorType: "MapTileError", source: `labels:${name}` }
      }));
    });
  }
}

function addMobileAttributionControl(map) {
  const control = L.control({ position: "bottomright" });
  control.onAdd = () => {
    const container = L.DomUtil.create("div", "map-mobile-attribution-control");
    const button = L.DomUtil.create("button", "map-mobile-attribution-button", container);
    button.type = "button";
    button.textContent = "i";
    button.setAttribute("aria-label", "Show map attribution");
    button.setAttribute("aria-expanded", "false");

    const panel = L.DomUtil.create("div", "map-mobile-attribution-panel", container);
    panel.id = "mapMobileAttributionPanel";
    panel.hidden = true;
    button.setAttribute("aria-controls", panel.id);

    const source = map.attributionControl?.getContainer();
    const syncAttribution = () => {
      if (!source) return;
      panel.replaceChildren(...Array.from(source.childNodes, (node) => node.cloneNode(true)));
    };
    syncAttribution();
    const observer = source && new MutationObserver(syncAttribution);
    observer?.observe(source, { childList: true, subtree: true });

    const setExpanded = (expanded) => {
      panel.hidden = !expanded;
      button.setAttribute("aria-expanded", String(expanded));
      button.setAttribute("aria-label", `${expanded ? "Hide" : "Show"} map attribution`);
    };
    L.DomEvent.disableClickPropagation(container);
    L.DomEvent.disableScrollPropagation(container);
    L.DomEvent.on(button, "click", () => setExpanded(button.getAttribute("aria-expanded") !== "true"));
    L.DomEvent.on(container, "keydown", (event) => {
      if (event.key !== "Escape" || button.getAttribute("aria-expanded") !== "true") return;
      setExpanded(false);
      button.focus();
    });
    return container;
  };
  control.addTo(map);
}

export function depthText(payload) {
  if (!payload?.available || !Number.isFinite(Number(payload.depthMeters))) return "";
  return formatUnitValue(Number(payload.depthMeters), "depth", "m", { decimals: 0 });
}

export function pointPopupHtml(coordinates, depth, status, overlayHtml = "") {
  const position = positionLabel(coordinates.latitude, coordinates.longitude);
  const air = document.querySelector("[data-gl-layer]")?.value === "air-temperature";
  // Depth comes from contours or the lake model, which cover every lake, so
  // a point with no depth at all is on land.
  const offLake = status === "ready" && depth?.available === false;
  const heading = status === "loading" ? "Looking up…" : air ? "Air temperature" : depthText(depth) ? `${depthText(depth)} deep` : status === "error" ? "Depth unavailable" : offLake ? "Not on the water" : "No depth found";
  const reading = offLake && !air ? "" : overlayHtml;
  return html`<article class="gl-card${status === "loading" ? " is-loading" : ""}">
    <header class="gl-card-head"><strong class="gl-card-title">${heading}</strong><span class="gl-card-sub">${position}</span></header>
    ${reading}
  </article>`;
}

export function panelSummaryText({ layer, metadata = {}, depth = 0 } = {}) {
  if (!layer) return "No layer shown";
  const label = LAYER_OPTIONS.find(([value]) => value === layer)?.[1] || layer;
  const depthPart = (layer === "temperature" || layer === "currents") && Number(depth) > 0.25 ? ` at ${greatLakesDepthLabel(Number(depth))}` : "";
  const validTime = metadata.validTime || (metadata.models || []).find((model) => model.validTime)?.validTime;
  const time = validTime ? friendlyTime(validTime) : "";
  const when = time ? time[0].toUpperCase() + time.slice(1) : "";
  return `${label}${depthPart}${when ? ` · ${when}` : ""}`;
}

function lakeNameFromCoordinates(latitude, longitude) {
  const match = Object.entries(GREAT_LAKES_MODEL_BOUNDS).find(([, bounds]) => {
    const [[south, west], [north, east]] = bounds;
    return latitude >= south && latitude <= north && longitude >= west && longitude <= east;
  });
  if (!match) return "Unknown";
  const modelName = match[0];
  const names = {
    LSOFS: "Lake Superior",
    LMHOFS: "Lake Michigan/Huron",
    LEOFS: "Lake Erie",
    LOOFS: "Lake Ontario"
  };
  return names[modelName] || modelName;
}

// One click shows the depth there plus whatever the active layer has at that point.
export async function showPointPopup(map, event) {
  const coordinates = { latitude: Number(event.latlng.lat), longitude: Number(event.latlng.lng) };
  if (!Number.isFinite(coordinates.latitude) || !Number.isFinite(coordinates.longitude)) return;
  const air = document.querySelector("[data-gl-layer]")?.value === "air-temperature";
  const popup = L.popup({ className: "gl-popup", minWidth: 240, maxWidth: 280 }).setLatLng(event.latlng).setContent(String(pointPopupHtml(coordinates, null, "loading"))).openOn(map);
  let depth = null, status = "loading", reading = "";
  const render = () => {
    if (map.hasLayer(popup)) popup.setContent(String(pointPopupHtml(coordinates, depth, status, reading)));
  };
  // Each result updates independently. A cold water-column download must not
  // hold back a depth that is already available (or hide a ready layer reading).
  await Promise.allSettled([
    Promise.resolve().then(() => air ? null : window.noaaGreatLakesApi.depth(coordinates)).then((value) => {
      depth = value;
      status = "ready";
      render();
    }, () => {
      status = "error";
      render();
    }),
    Promise.resolve().then(() => window.getGreatLakesMapInspection?.(coordinates) || "").then((value) => {
      reading = value;
      render();
    })
  ]);
}

function setPanelOpen(open) {
  const panel = document.querySelector("#panel");
  const toggle = document.querySelector("[data-panel-toggle]");
  panel?.classList.toggle("is-collapsed", !open);
  toggle?.setAttribute("aria-expanded", String(open));
  if (toggle) toggle.textContent = open ? "Hide" : "Layers";
  try { localStorage.setItem(PANEL_STORAGE_KEY, open ? "1" : "0"); } catch { /* storage unavailable */ }
}

function savedPanelOpen() {
  try {
    const saved = localStorage.getItem(PANEL_STORAGE_KEY);
    // Small or short screens (phones, either way up) start with the panel closed so the map is visible.
    return saved === null ? window.matchMedia("(min-width: 700px) and (min-height: 501px)").matches : saved === "1";
  } catch { return true; }
}

function setupPanel(map) {
  const basemap = document.querySelector("[data-basemap]");
  setHtml(basemap, html`${Object.entries(BASEMAPS).map(([key, config]) => html`<option value="${key}">${config.label}</option>`)}`);
  basemap.value = savedBasemap();
  basemap.addEventListener("change", () => {
    setBasemap(map, basemap.value);
    try { localStorage.setItem(BASEMAP_STORAGE_KEY, basemap.value); } catch { /* storage unavailable */ }
  });

  const units = document.querySelector("[data-units]");
  units.value = currentUnitSystem();
  units.addEventListener("change", () => setUnitSystem(units.value));

  document.addEventListener("change", (event) => {
    if (event.target?.matches?.("[data-temperature-colors]")) {
      setTemperatureColorMode(event.target.checked ? "dynamic" : "fixed");
    }
  });

  const panel = document.querySelector("#panel");
  document.querySelector("[data-panel-toggle]")?.addEventListener("click", (event) => {
    event.stopPropagation();
    setPanelOpen(panel.classList.contains("is-collapsed"));
  });
  // On a phone the whole header bar opens and closes the sheet.
  document.querySelector("[data-panel-header]")?.addEventListener("click", () => {
    if (isNarrowScreen()) setPanelOpen(panel.classList.contains("is-collapsed"));
  });
  setPanelOpen(savedPanelOpen());

  // Map controls and attribution stay just above the bottom sheet.
  const syncSheetOffset = () => {
    const rect = panel.getBoundingClientRect();
    document.documentElement.style.setProperty("--sheet-offset", `${Math.max(0, Math.round(window.innerHeight - rect.top + 6))}px`);
  };
  if (typeof ResizeObserver === "function") new ResizeObserver(syncSheetOffset).observe(panel);
  window.addEventListener("resize", syncSheetOffset);
  syncSheetOffset();

  const summary = document.querySelector("[data-panel-summary]");
  const showSummary = (event) => {
    if (summary) summary.textContent = panelSummaryText(event.detail || {});
  };
  document.addEventListener("great-lakes-layer-loaded", showSummary);
  // While the forecast animation plays, the summary follows the frame on screen.
  document.addEventListener("great-lakes-frame-shown", showSummary);
}

function forceMapTutorial() {
  return new URLSearchParams(window.location.search).get("tutorial") === "1";
}

function shouldShowMapTutorial() {
  if (forceMapTutorial()) return true;
  try { return localStorage.getItem(MAP_HINTS_STORAGE_KEY) !== "2"; }
  catch { return true; }
}

function setupMapProfileHint(tutorialWillShow = false) {
  const hint = document.querySelector("[data-map-profile-hint]");
  const dismiss = hint?.querySelector("[data-map-profile-hint-dismiss]");
  const mapNode = document.querySelector("#map");
  if (!hint || !dismiss || !mapNode) return;

  const isSeen = () => {
    try { return localStorage.getItem(MAP_PROFILE_HINT_SEEN_KEY) === "1"; }
    catch { return false; }
  };
  const rememberHint = () => {
    try { localStorage.setItem(MAP_PROFILE_HINT_SEEN_KEY, "1"); }
    catch { /* The hint still works for this visit when storage is unavailable. */ }
  };
  if (isSeen()) return;

  const tour = document.querySelector("[data-map-tour]");
  let mapReady = mapNode.getAttribute("aria-busy") !== "true";
  let tutorialComplete = !tutorialWillShow || !tour;
  let tourObserver = null;

  const showIfReady = () => {
    if (!mapReady || !tutorialComplete || (tour && !tour.hidden) || isSeen()) return;
    hint.hidden = false;
    rememberHint();
    tourObserver?.disconnect();
  };

  if (!mapReady) {
    document.addEventListener("great-lakes-map-ready", () => {
      mapReady = true;
      showIfReady();
    }, { once: true });
  }

  if (tutorialWillShow && tour) {
    tourObserver = new MutationObserver(() => {
      if (tour.hidden) tutorialComplete = true;
      showIfReady();
    });
    tourObserver.observe(tour, { attributes: true, attributeFilter: ["hidden"] });
  }

  dismiss.addEventListener("click", () => {
    hint.hidden = true;
    tourObserver?.disconnect();
    rememberHint();
  });

  showIfReady();
}

function setupFirstVisitHint(map, tutorialWillShow = shouldShowMapTutorial()) {
  const tour = document.querySelector("[data-map-tour]");
  const spotlight = document.querySelector("[data-map-tour-spotlight]");
  if (!tour || !spotlight || !tutorialWillShow) return;
  const forceTutorial = forceMapTutorial();
  const layerSelector = document.querySelector("[data-gl-layer]");

  const panel = document.querySelector("#panel");
  const legendSelectors = [
    ".great-lakes-current-legend",
    ".great-lakes-legend",
    ".great-lakes-thermocline-legend",
    ".great-lakes-wave-legend",
    ".great-lakes-wind-legend",
    ".great-lakes-upwelling-legend",
    "[data-bathymetry-raster-legend]"
  ];
  const findVisibleLegend = () => legendSelectors
    .map((selector) => document.querySelector(selector))
    .find((element) => {
      if (!element) return false;
      const rect = element.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && window.getComputedStyle(element).display !== "none";
    });
  const steps = [
    ...(panel?.classList.contains("is-collapsed") ? [{
      target: "[data-panel-toggle]",
      title: "Open your map controls",
      description: "Click Layers to open the controls and get started.",
      advanceOn: "click",
      requiresAction: true
    }] : []),
    {
      target: "[data-gl-layer]",
      title: "Choose a layer",
      description: "Choose Water temperature, Currents, or Thermocline depth to continue.",
      advanceOn: "change",
      requiresAction: true
    },
    {
      target: "#map",
      title: "Read a spot",
      description: "Click inside the highlighted box to open a reading for your selected layer.",
      advanceOn: "map"
    },
    {
      target: () => map.getContainer().querySelector(layerSelector?.value === "currents" ? "[data-gl-current-profile-lat]" : "[data-gl-profile-lat]"),
      title: () => layerSelector?.value === "currents" ? "Explore the current profile" : "Open the water column",
      description: () => layerSelector?.value === "currents"
        ? "When the reading appears, click Current by depth to compare flow through the water. Close the profile to continue."
        : "When the reading appears, click Water column to compare temperature through the water. Close the profile to continue.",
      advanceOn: "profile",
      requiresAction: true
    },
    {
      target: "[data-gl-forecast-choices]",
      title: "Choose a forecast time",
      description: "Choose how far into the forecast to look — hourly through the next 6 hours, then 12, 24, and 48 hours, or drag the slider through five days. The Play button below animates the forecast.",
      advanceOn: "change"
    },
    {
      target: "[data-gl-timeline]",
      title: "Animate the forecast",
      description: "Click Play to animate up to five days, or drag the timeline to move between frames. Waves animate through 48 hours. Click Next when you're ready.",
      advanceOn: "next"
    },
    {
      target: "[data-map-overlay-controls]",
      title: "Explore lake depth",
      description: "Click Show depth contours to trace the lake bottom, or Show depth shading to see depth as color. Try either or both, then click Next.",
      advanceOn: "next"
    },
    {
      target: "[data-gl-depth]",
      title: "Notice the depth control",
      description: "This slider changes the water depth shown on the map. You don’t need to move it now—just know it’s here when you want to explore below the surface. Click Next to continue.",
      advanceOn: "next"
    },
    {
      target: () => findVisibleLegend() || document.querySelector("[data-gl-status]"),
      title: () => findVisibleLegend() ? "Read the legend" : "Read the layer status",
      description: () => findVisibleLegend()
        ? "The color key explains the values shown for your selected layer. Click Finish to start exploring."
        : "This layer is in map-only mode, so it has no color key. The message below shows its current status. Click Finish to start exploring.",
      advanceOn: "next"
    }
  ];
  const title = tour.querySelector("[data-map-tour-title]");
  const description = tour.querySelector("[data-map-tour-description]");
  const progress = tour.querySelector("[data-map-tour-progress]");
  const progressTrack = tour.querySelector("[role='progressbar']");
  const progressFill = tour.querySelector("[data-map-tour-progress-fill]");
  const backButton = tour.querySelector("[data-map-tour-back]");
  const nextButton = tour.querySelector("[data-map-tour-next]");
  const skipButton = tour.querySelector("[data-map-tour-skip]");
  const originalLayerOptionStates = layerSelector
    ? [...layerSelector.options].map((option) => [option, option.disabled])
    : [];
  let stepIndex = 0;
  let isOpen = false;
  let lockedMapHandlers = null;
  let targetObserver = null;
  let profileDialogObserver = null;
  let focusProfileActionWhenReady = false;
  let mapStepAdvanceQueued = false;

  const isStepSkipped = (step) => typeof step.skipWhen === "function" && step.skipWhen();

  const setTutorialLayerChoices = (restricted) => {
    if (!layerSelector) return;
    for (const [option, wasDisabled] of originalLayerOptionStates) {
      option.disabled = wasDisabled || (restricted && !MAP_TUTORIAL_ALLOWED_LAYERS.has(option.value));
    }
    document.body.classList.toggle("map-tutorial-layer-options-restricted", restricted);
  };

  const resolveTarget = (step) => typeof step.target === "function"
    ? step.target()
    : document.querySelector(step.target);

  const setMapInteractionLocked = (locked) => {
    if (locked) {
      if (lockedMapHandlers) return;
      const handlerNames = ["dragging", "touchZoom", "doubleClickZoom", "scrollWheelZoom", "boxZoom", "keyboard", "tap"];
      lockedMapHandlers = handlerNames
        .map((name) => map[name])
        .filter((handler) => handler && typeof handler.enabled === "function")
        .map((handler) => [handler, handler.enabled()]);
      for (const [handler] of lockedMapHandlers) handler.disable();
      document.body.classList.add("map-tutorial-map-locked");
      return;
    }

    if (lockedMapHandlers) {
      for (const [handler, wasEnabled] of lockedMapHandlers) {
        if (wasEnabled) handler.enable();
        else handler.disable();
      }
      lockedMapHandlers = null;
    }
    document.body.classList.remove("map-tutorial-map-locked");
  };

  const isInsideMapSpotlight = (point) => {
    const center = map.latLngToContainerPoint(MAP_TUTORIAL_SPOTLIGHT_CENTER);
    return Math.abs(point.x - center.x) <= MAP_TUTORIAL_SPOTLIGHT_WIDTH / 2
      && Math.abs(point.y - center.y) <= MAP_TUTORIAL_SPOTLIGHT_HEIGHT / 2;
  };

  const positionSpotlight = () => {
    if (!isOpen) return;
    const step = steps[stepIndex];
    const target = resolveTarget(step);
    if (!target) { spotlight.hidden = true; return; }
    let rect;
    if (step.target === "#map") {
      const mapBounds = map.getContainer().getBoundingClientRect();
      const point = map.latLngToContainerPoint(MAP_TUTORIAL_SPOTLIGHT_CENTER);
      const centerX = mapBounds.left + point.x;
      const centerY = mapBounds.top + point.y;
      rect = {
        left: centerX - MAP_TUTORIAL_SPOTLIGHT_WIDTH / 2,
        top: centerY - MAP_TUTORIAL_SPOTLIGHT_HEIGHT / 2,
        width: MAP_TUTORIAL_SPOTLIGHT_WIDTH,
        height: MAP_TUTORIAL_SPOTLIGHT_HEIGHT
      };
    } else {
      rect = target.getBoundingClientRect();
    }
    if (!rect.width || !rect.height) { spotlight.hidden = true; return; }
    const padding = step.target === "#map" ? 0 : 6;
    spotlight.style.left = `${Math.max(4, rect.left - padding)}px`;
    spotlight.style.top = `${Math.max(4, rect.top - padding)}px`;
    spotlight.style.width = `${Math.min(window.innerWidth - 8, rect.width + padding * 2)}px`;
    spotlight.style.height = `${Math.min(window.innerHeight - 8, rect.height + padding * 2)}px`;
    spotlight.hidden = false;
    if (focusProfileActionWhenReady && step.advanceOn === "profile") {
      focusProfileActionWhenReady = false;
      target.focus?.({ preventScroll: true });
    }
  };

  const syncTutorialPanelVisibility = (step) => {
    document.body.classList.toggle(
      "map-tutorial-panel-stowed",
      isNarrowScreen() && (step.advanceOn === "map" || step.advanceOn === "profile")
    );
  };

  const syncResponsiveTutorialLayout = () => {
    syncTutorialPanelVisibility(steps[stepIndex]);
    positionSpotlight();
  };

  const renderStep = (moveFocus = false) => {
    const step = steps[stepIndex];
    const visibleSteps = steps.filter((candidate) => !isStepSkipped(candidate));
    const number = visibleSteps.indexOf(step) + 1;
    title.textContent = typeof step.title === "function" ? step.title() : step.title;
    description.textContent = typeof step.description === "function" ? step.description() : step.description;
    progress.textContent = `${number} of ${visibleSteps.length}`;
    progressTrack.setAttribute("aria-valuemax", String(visibleSteps.length));
    progressTrack.setAttribute("aria-valuenow", String(number));
    progressFill.style.transform = `scaleX(${number / visibleSteps.length})`;
    backButton.hidden = stepIndex === 0;
    nextButton.textContent = stepIndex === steps.length - 1 ? "Finish" : "Next";
    nextButton.hidden = step.advanceOn === "map" || step.requiresAction === true;
    syncTutorialPanelVisibility(step);
    setMapInteractionLocked(step.advanceOn === "map");
    targetObserver?.disconnect();
    targetObserver = null;
    if (step.advanceOn === "profile") {
      const popupPane = map.getPane("popupPane");
      if (popupPane) {
        targetObserver = new MutationObserver(positionSpotlight);
        targetObserver.observe(popupPane, { childList: true, subtree: true });
      }
    }
    focusProfileActionWhenReady = moveFocus && step.advanceOn === "profile";
    if (step.advanceOn === "map") {
      const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
      map.flyTo(MAP_TUTORIAL_SPOTLIGHT_CENTER, MAP_TUTORIAL_SPOTLIGHT_ZOOM, {
        animate: !reduceMotion,
        duration: 0.8
      });
    }
    const target = resolveTarget(step);
    if (step.target !== "#map") target?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    requestAnimationFrame(() => {
      positionSpotlight();
      if (moveFocus) {
        const focusTarget = step.advanceOn === "profile"
          ? skipButton
          : !nextButton.hidden ? nextButton : !backButton.hidden ? backButton : skipButton;
        focusTarget?.focus({ preventScroll: true });
      }
    });
  };

  const saveCompletion = () => {
    if (forceTutorial) return;
    try { localStorage.setItem(MAP_HINTS_STORAGE_KEY, "2"); } catch { /* The tutorial can still be dismissed for this visit. */ }
  };

  const finish = () => {
    if (!isOpen) return;
    isOpen = false;
    saveCompletion();
    setMapInteractionLocked(false);
    setTutorialLayerChoices(false);
    document.body.classList.remove("map-tutorial-panel-stowed");
    targetObserver?.disconnect();
    profileDialogObserver?.disconnect();
    tour.hidden = true;
    spotlight.hidden = true;
    window.removeEventListener("resize", syncResponsiveTutorialLayout);
    document.removeEventListener("scroll", positionSpotlight, true);
    document.removeEventListener("click", handleTargetClick, true);
    document.removeEventListener("click", handleMapStepClickCapture, true);
    document.removeEventListener("change", handleTargetChange);
    document.removeEventListener("keydown", handleTourKeydown);
    map.off("move zoom", positionSpotlight);
    map.off("click", handleMapClick);
  };

  const next = () => {
    let nextIndex = stepIndex + 1;
    while (nextIndex < steps.length && isStepSkipped(steps[nextIndex])) nextIndex += 1;
    if (nextIndex >= steps.length) { finish(); return; }
    stepIndex = nextIndex;
    renderStep(true);
  };

  const back = () => {
    let previousIndex = stepIndex - 1;
    while (previousIndex >= 0 && isStepSkipped(steps[previousIndex])) previousIndex -= 1;
    if (previousIndex < 0) return;
    stepIndex = previousIndex;
    renderStep(true);
  };

  function handleTargetClick(event) {
    const step = steps[stepIndex];
    if (step.advanceOn !== "click" && step.advanceOn !== "profile") return;
    const target = resolveTarget(step);
    if (!target?.contains(event.target)) return;
    if (step.advanceOn === "click") {
      next();
      return;
    }

    profileDialogObserver?.disconnect();
    profileDialogObserver = null;
    const dialogSelector = layerSelector?.value === "currents" ? ".great-lakes-current-dialog" : ".water-column-dialog";
    const attachCloseHandler = () => {
      if (!isOpen || steps[stepIndex].advanceOn !== "profile") return true;
      const dialog = document.querySelector(dialogSelector);
      if (!dialog) return false;
      dialog.addEventListener("close", () => {
        if (isOpen && steps[stepIndex].advanceOn === "profile") next();
      }, { once: true });
      return true;
    };
    if (!attachCloseHandler()) {
      profileDialogObserver = new MutationObserver(() => {
        if (attachCloseHandler()) {
          profileDialogObserver?.disconnect();
          profileDialogObserver = null;
        }
      });
      profileDialogObserver.observe(document.body, { childList: true, subtree: true });
    }
  }

  function handleTargetChange(event) {
    const step = steps[stepIndex];
    if (step.advanceOn !== "change" || !event.target.matches(step.target)) return;
    next();
  }

  function handleMapStepClickCapture(event) {
    if (steps[stepIndex].advanceOn !== "map" || !map.getContainer().contains(event.target)) return;
    const bounds = map.getContainer().getBoundingClientRect();
    const point = { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
    if (isInsideMapSpotlight(point)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  function handleMapClick(event) {
    if (steps[stepIndex].advanceOn !== "map" || mapStepAdvanceQueued || !isInsideMapSpotlight(event.containerPoint)) return;
    mapStepAdvanceQueued = true;
    requestAnimationFrame(() => {
      mapStepAdvanceQueued = false;
      if (isOpen && steps[stepIndex].advanceOn === "map") next();
    });
  }

  function handleTourKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault();
      finish();
    }
  }

  tour.querySelector("[data-map-tour-skip]")?.addEventListener("click", finish);
  backButton?.addEventListener("click", back);
  nextButton?.addEventListener("click", next);
  document.addEventListener("click", handleTargetClick, true);
  document.addEventListener("click", handleMapStepClickCapture, true);
  document.addEventListener("change", handleTargetChange);
  document.addEventListener("keydown", handleTourKeydown);
  window.addEventListener("resize", syncResponsiveTutorialLayout);
  document.addEventListener("scroll", positionSpotlight, true);
  map.on("move zoom", positionSpotlight);
  map.on("click", handleMapClick);

  setTutorialLayerChoices(true);
  isOpen = true;
  tour.hidden = false;
  spotlight.hidden = false;
  renderStep();
  skipButton.focus({ preventScroll: true });
}

function setupBathymetryContours(map) {
  const checkbox = document.querySelector("[data-bathymetry-contours]");
  const status = document.querySelector("[data-bathymetry-contours-status]");
  if (!checkbox) return;

  const requests = new Map();
  const layerCache = new Map();
  const labelLayer = L.layerGroup();
  let activeLayer = null;
  let activeViewKey = "";
  let majorContourLayers = [];
  let shownStatus = null;
  let requestSequence = 0;
  let labelFrame = null;
  const overviewZoom = 6;
  const lakeOrder = ["ontario", "erie", "michigan", "huron", "superior"];

  const updateStatus = (message, isError = false) => {
    if (!status) return;
    status.textContent = message;
    status.classList.toggle("is-loading", message.startsWith("Loading "));
    status.classList.toggle("is-error", isError);
  };
  const contourInterval = () => formatUnitValue(10, "depth", "m", { decimals: currentUnitSystem() === "nautical" ? 1 : 0 });

  const loadData = (slug, detail) => {
    const key = slug + ":" + detail;
    if (!requests.has(key)) {
      const request = fetch("/api/v1/bathymetry/" + slug + "-contours?detail=" + detail)
        .then((response) => {
          if (!response.ok) throw new Error("Could not load the contour data.");
          return response.json();
        });
      requests.set(key, request);
      request.then(() => requests.delete(key), () => requests.delete(key));
    }
    return requests.get(key);
  };

  const ensurePanes = () => {
    if (!map.getPane("bathymetryContours")) {
      const pane = map.createPane("bathymetryContours");
      pane.style.zIndex = "450";
    }
    if (!map.getPane("bathymetryContourLabels")) {
      const pane = map.createPane("bathymetryContourLabels");
      pane.style.zIndex = "460";
      pane.style.pointerEvents = "none";
    }
  };

  const layerForLake = (data) => {
    ensurePanes();
    const majorLayers = [];
    const renderer = L.canvas({ pane: "bathymetryContours", padding: 0.15 });
    const layer = L.geoJSON(data, {
      pane: "bathymetryContours",
      renderer,
      style: (feature) => {
        const major = Number(feature.properties?.DEPTH) % 10 === 0;
        return { color: major ? "#a0dcff" : "#5798b5", weight: major ? 1.2 : 0.65, opacity: major ? 0.92 : 0.58 };
      },
      smoothFactor: 1.2,
      interactive: false,
      onEachFeature: (feature, featureLayer) => {
        if (Number(feature.properties?.DEPTH) % 10 === 0) majorLayers.push(featureLayer);
      }
    });
    // Remove the renderer with its paths when switching lakes/detail levels;
    // detached cached contours must not leave canvases listening to map moves.
    return { layer: L.layerGroup([layer, renderer]), majorLayers };
  };

  const loadLayer = (slug, detail) => {
    const key = slug + ":" + detail;
    if (!layerCache.has(key)) {
      const request = loadData(slug, detail).then(layerForLake);
      layerCache.set(key, request);
      request.catch(() => {
        if (layerCache.get(key) === request) layerCache.delete(key);
      });
    }
    return layerCache.get(key);
  };

  const updateLabels = () => {
    labelLayer.clearLayers();
    if (!checkbox.checked || !activeLayer || !map.hasLayer(activeLayer) || map.getZoom() < 13) return;

    const viewport = map.getBounds();
    const size = map.getSize();
    const origin = map.containerPointToLayerPoint(L.point(0, 0));
    const labelCandidates = [];
    for (const line of majorContourLayers) {
      if (!viewport.intersects(line.getBounds())) continue;
      // Leaflet has already projected, clipped, and simplified these parts for
      // drawing. Reuse them instead of projecting the entire lake for labels.
      const parts = line._parts || [];
      let best = null;

      for (const part of parts) {
        const points = part.map((point) => point.subtract(origin));
        let run = [];
        const finishRun = () => {
          if (run.length < 2) { run = []; return; }
          const length = run.reduce((sum, point, index) => index ? sum + point.distanceTo(run[index - 1]) : 0, 0);
          if (!best || length > best.length) {
            let remaining = length / 2;
            for (let index = 1; index < run.length; index += 1) {
              const segmentLength = run[index - 1].distanceTo(run[index]);
              if (remaining <= segmentLength) {
                const fraction = segmentLength ? remaining / segmentLength : 0;
                best = {
                  length,
                  point: L.point(
                    run[index - 1].x + (run[index].x - run[index - 1].x) * fraction,
                    run[index - 1].y + (run[index].y - run[index - 1].y) * fraction
                  )
                };
                break;
              }
              remaining -= segmentLength;
            }
          }
          run = [];
        };

        for (const point of points) {
          const inside = point.x >= 0 && point.x <= size.x && point.y >= 0 && point.y <= size.y;
          if (!inside) { finishRun(); continue; }
          if (run.length && point.distanceTo(run[run.length - 1]) < 220) run.push(point);
          else { finishRun(); run = [point]; }
        }
        finishRun();
      }

      if (best && best.length >= 44) {
        labelCandidates.push({ depthMeters: Number(line.feature?.properties?.DEPTH), ...best });
      }
    }

    labelCandidates.sort((left, right) => right.length - left.length);
    const placed = [];
    for (const candidate of labelCandidates) {
      const label = formatUnitValue(candidate.depthMeters, "depth", "m", { decimals: 0 });
      const width = label.length * 7 + 8;
      const point = candidate.point;
      if (point.x < width / 2 + 8 || point.x > size.x - width / 2 - 8 || point.y < 14 || point.y > size.y - 14) continue;
      const box = { left: point.x - width / 2, right: point.x + width / 2, top: point.y - 9, bottom: point.y + 9 };
      if (placed.some((other) => box.left < other.right + 10 && box.right + 10 > other.left && box.top < other.bottom + 7 && box.bottom + 7 > other.top)) continue;
      placed.push(box);
      labelLayer.addLayer(L.marker(map.containerPointToLatLng(point), {
        pane: "bathymetryContourLabels",
        interactive: false,
        keyboard: false,
        icon: L.divIcon({ className: "contour-depth-label", html: "<span>" + label + "</span>", iconSize: [width, 18], iconAnchor: [width / 2, 9] })
      }));
    }
  };

  const scheduleLabels = () => {
    if (labelFrame !== null) return;
    labelFrame = requestAnimationFrame(() => {
      labelFrame = null;
      updateLabels();
    });
  };

  const showContoursForCurrentView = async () => {
    const lake = mapCenterLake(map);
    const focusedSlug = lake.replace(/^Lake /, "").toLowerCase();
    const overview = map.getZoom() <= overviewZoom;
    const slugs = overview
      ? [focusedSlug, ...lakeOrder].filter((slug, index, all) => slug && all.indexOf(slug) === index)
      : (focusedSlug ? [focusedSlug] : []);
    const zoom = map.getZoom();
    const detail = zoom <= 6 ? "6" : zoom <= 9 ? "9" : zoom <= 12 ? "12" : "full";
    // The overview contains the same lakes when its center crosses a border.
    // Keep its key stable so a pan does not detach and re-add all five layers.
    const viewKey = slugs.length ? [...slugs].sort().join(",") + ":" + detail : "";
    if (viewKey && activeViewKey === viewKey && activeLayer && map.hasLayer(activeLayer)) {
      scheduleLabels();
      return;
    }
    const sequence = ++requestSequence;

    activeLayer?.remove();
    labelLayer.remove();
    labelLayer.clearLayers();
    activeLayer = null;
    majorContourLayers = [];

    if (!viewKey) {
      activeViewKey = "";
      shownStatus = null;
      updateStatus("Move the map over a Great Lake to load its contours.");
      return;
    }

    activeViewKey = viewKey;
    shownStatus = null;
    activeLayer = L.layerGroup().addTo(map);
    labelLayer.addTo(map);
    majorContourLayers = [];
    updateStatus(overview ? "Loading contours for all five Great Lakes…" : "Loading " + lake + " contours…");
    const failed = [];

    for (const [index, slug] of slugs.entries()) {
      try {
        const entry = await loadLayer(slug, detail);
        if (!checkbox.checked || sequence !== requestSequence || activeViewKey !== viewKey) return;
        activeLayer.addLayer(entry.layer);
        majorContourLayers.push(...entry.majorLayers);
        scheduleLabels();
        if (overview && index < slugs.length - 1) {
          updateStatus("Loading contours for all five Great Lakes… (" + (index + 1) + " of five loaded)");
        }
      } catch {
        if (sequence !== requestSequence) return;
        failed.push(slug);
      }
    }

    if (sequence !== requestSequence) return;
    if (failed.length) {
      const names = failed.map((slug) => "Lake " + slug[0].toUpperCase() + slug.slice(1));
      if (!overview) {
        activeLayer.remove();
        labelLayer.remove();
        activeLayer = null;
        activeViewKey = "";
        checkbox.checked = false;
        updateStatus("Could not load " + names[0] + " contours. Try again.", true);
      } else {
        updateStatus("Could not load " + names.join(", ") + " contours. Other lake contours are showing.", true);
      }
    } else {
      shownStatus = () => overview
        ? "Showing depth contours for all five Great Lakes. Lake Superior uses " + contourInterval() + " intervals."
        : "Showing " + lake + " depth contours" + (slugs[0] === "superior" ? " (" + contourInterval() + " intervals)" : "") + ".";
      updateStatus(shownStatus());
    }
  };

  checkbox.addEventListener("change", () => {
    if (!checkbox.checked) {
      requestSequence += 1;
      activeLayer?.remove();
      labelLayer.remove();
      labelLayer.clearLayers();
      activeLayer = null;
      activeViewKey = "";
      majorContourLayers = [];
      shownStatus = null;
      updateStatus("");
      return;
    }
    void showContoursForCurrentView();
  });

  map.on("moveend", () => {
    if (checkbox.checked) void showContoursForCurrentView();
    else scheduleLabels();
  });
  map.on("zoomend", scheduleLabels);
  document.addEventListener("glc-units-changed", () => {
    scheduleLabels();
    if (checkbox.checked && shownStatus) updateStatus(shownStatus());
  });
}

function setupBathymetryRaster(map) {
  const checkbox = document.querySelector("[data-bathymetry-raster]");
  const status = document.querySelector("[data-bathymetry-raster-status]");
  if (!checkbox) return;

  const lakeOrder = ["ontario", "erie", "michigan", "huron", "superior"];
  const overlayCache = new Map();
  let manifestRequest = null;
  let activeLayer = null;
  let activeViewKey = "";
  let requestSequence = 0;
  let shownStatus = null;

  const updateStatus = (message, isError = false) => {
    if (!status) return;
    status.textContent = message;
    status.classList.toggle("is-loading", message.startsWith("Loading "));
    status.classList.toggle("is-error", isError);
  };
  const depthUnitName = () => ({ ft: "feet", m: "metres", fm: "fathoms" }[unitPreference("depth")] || unitPreference("depth"));
  const updateDepthLegend = () => {
    const minimum = document.querySelector("[data-bathymetry-raster-depth-min]");
    const maximum = document.querySelector("[data-bathymetry-raster-depth-max]");
    const legend = document.querySelector("[data-bathymetry-raster-legend]");
    if (minimum) minimum.textContent = formatUnitValue(0, "depth", "m", { decimals: 0 });
    if (maximum) maximum.textContent = formatUnitValue(400, "depth", "m", { decimals: 0 });
    if (legend && minimum && maximum) legend.setAttribute("aria-label", "Raster depth scale from " + minimum.textContent + " to " + maximum.textContent);
    for (const entry of overlayCache.values()) {
      entry.layer.getElement()?.setAttribute("alt", "NOAA " + entry.name + " bathymetry, depth in " + depthUnitName());
    }
  };
  updateDepthLegend();

  const loadManifest = () => {
    if (!manifestRequest) {
      manifestRequest = fetch("/static/data/bathymetry/manifest.json").then((response) => {
        if (!response.ok) throw new Error("Could not load the bathymetry raster index.");
        return response.json();
      });
      manifestRequest.catch(() => { manifestRequest = null; });
    }
    return manifestRequest;
  };

  const ensurePane = () => {
    if (!map.getPane("bathymetryRaster")) {
      const pane = map.createPane("bathymetryRaster");
      pane.style.zIndex = "420";
    }
  };

  const loadOverlay = (slug, lake) => {
    if (!overlayCache.has(slug)) {
      ensurePane();
      const bounds = L.latLngBounds(lake.bounds[0], lake.bounds[1]);
      const layer = L.imageOverlay(lake.image, bounds, {
        pane: "bathymetryRaster",
        opacity: 0.88,
        interactive: false,
        alt: "NOAA " + lake.name + " bathymetry, depth in " + depthUnitName()
      });
      const request = new Promise((resolve, reject) => {
        layer.once("load", () => resolve(layer));
        layer.once("error", () => reject(new Error("Could not load " + lake.name + " bathymetry.")));
      });
      overlayCache.set(slug, { layer, request, name: lake.name });
      request.catch(() => {
        if (overlayCache.get(slug)?.request === request) overlayCache.delete(slug);
      });
    }
    const entry = overlayCache.get(slug);
    activeLayer.addLayer(entry.layer);
    return entry.request;
  };

  const showRasterForCurrentView = async () => {
    const lake = mapCenterLake(map);
    const focusedSlug = lake.replace(/^Lake /, "").toLowerCase();
    const overview = map.getZoom() <= 6;
    const slugs = overview
      ? [focusedSlug, ...lakeOrder].filter((slug, index, all) => slug && all.indexOf(slug) === index)
      : (focusedSlug ? [focusedSlug] : []);
    const viewKey = slugs.join(",");
    if (viewKey && activeViewKey === viewKey && activeLayer && map.hasLayer(activeLayer)) return;

    const sequence = ++requestSequence;
    activeLayer?.clearLayers();
    activeLayer?.remove();
    activeLayer = null;

    if (!viewKey) {
      activeViewKey = "";
      shownStatus = null;
      updateStatus("Move the map over a Great Lake to load its bathymetry raster.");
      return;
    }

    activeViewKey = viewKey;
    shownStatus = null;
    activeLayer = L.layerGroup().addTo(map);
    updateStatus(overview ? "Loading bathymetry for all five Great Lakes…" : "Loading " + lake + " bathymetry…");

    try {
      const manifest = await loadManifest();
      if (!checkbox.checked || sequence !== requestSequence || activeViewKey !== viewKey) return;
      const failed = [];
      for (const [index, slug] of slugs.entries()) {
        const lakeData = manifest.lakes?.[slug];
        if (!lakeData) {
          failed.push(slug);
          continue;
        }
        try {
          await loadOverlay(slug, lakeData);
          if (!checkbox.checked || sequence !== requestSequence || activeViewKey !== viewKey) return;
          if (overview && index < slugs.length - 1) {
            updateStatus("Loading bathymetry for all five Great Lakes… (" + (index + 1) + " of five loaded)");
          }
        } catch {
          if (sequence !== requestSequence) return;
          failed.push(slug);
        }
      }
      if (sequence !== requestSequence) return;
      if (failed.length) {
        const names = failed.map((slug) => manifest.lakes?.[slug]?.name || "Lake " + slug[0].toUpperCase() + slug.slice(1));
        if (!overview) {
          activeLayer?.clearLayers();
          activeLayer?.remove();
          activeLayer = null;
          activeViewKey = "";
          checkbox.checked = false;
          updateStatus("Could not load " + names[0] + " bathymetry. Try again.", true);
        } else {
          updateStatus("Could not load " + names.join(", ") + " bathymetry. Other lake rasters are showing.", true);
        }
      } else {
        shownStatus = () => overview
          ? "Showing bathymetry for all five Great Lakes. Depths are in " + depthUnitName() + "."
          : "Showing " + lake + " bathymetry. Depths are in " + depthUnitName() + ".";
        updateStatus(shownStatus());
      }
    } catch {
      if (sequence !== requestSequence) return;
      activeLayer?.clearLayers();
      activeLayer?.remove();
      activeLayer = null;
      activeViewKey = "";
      shownStatus = null;
      checkbox.checked = false;
      updateStatus("Could not load the bathymetry raster data. Try again.", true);
    }
  };

  checkbox.addEventListener("change", () => {
    if (!checkbox.checked) {
      requestSequence += 1;
      activeLayer?.clearLayers();
      activeLayer?.remove();
      activeLayer = null;
      activeViewKey = "";
      shownStatus = null;
      updateStatus("");
      return;
    }
    void showRasterForCurrentView();
  });

  map.on("moveend", () => {
    if (checkbox.checked) void showRasterForCurrentView();
  });
  document.addEventListener("glc-units-changed", () => {
    updateDepthLegend();
    if (checkbox.checked && shownStatus) updateStatus(shownStatus());
  });
}
function isNarrowScreen() {
  return window.matchMedia("(max-width: 699px), (max-height: 500px)").matches;
}

// Apply a shared link's layer choices before the first layer loads.
function applyLinkedControls(linked) {
  const layer = document.querySelector("[data-gl-layer]");
  if (layer && linked.layer !== undefined) layer.value = linked.layer;
  const forecast = document.querySelector("[data-gl-forecast]");
  if (forecast && linked.forecast !== undefined) forecast.value = linked.forecast;
  if (linked.time) setGreatLakesHistoryTime(linked.time);
  if (linked.targetC !== undefined) setTargetTemperature(linked.targetC);
  const selection = document.querySelector("[data-gl-target-selection]");
  if (selection && linked.selection) selection.value = linked.selection;
  const coverage = document.querySelector("[data-gl-air-coverage]");
  if (coverage && linked.coverage) coverage.value = linked.coverage;
  const contours = document.querySelector("[data-bathymetry-contours]");
  if (contours && linked.contours) {
    contours.checked = true;
    contours.dispatchEvent(new Event("change", { bubbles: true }));
  }
  const bathymetry = document.querySelector("[data-bathymetry-raster]");
  if (bathymetry && linked.bathymetry) {
    bathymetry.checked = true;
    bathymetry.dispatchEvent(new Event("change", { bubbles: true }));
  }
  const slider = document.querySelector("[data-gl-depth]");
  if (slider && linked.depth !== undefined) {
    slider.value = String(sliderFromDepth(linked.depth));
    slider.dispatchEvent(new Event("input", { bubbles: true }));
  }
  syncGreatLakesLayerControls();
}

export function currentViewHash(map) {
  const center = map.getCenter();
  const slider = document.querySelector("[data-gl-depth]");
  return viewHash({
    center: [center.lat, center.lng],
    zoom: map.getZoom(),
    layer: document.querySelector("[data-gl-layer]")?.value || "",
    forecast: document.querySelector("[data-gl-forecast]")?.value || "0",
    time: greatLakesHistoryTime(),
    depth: slider ? greatLakesDepthFromSlider(slider.value) : 0,
    targetC: targetTemperatureC,
    selection: document.querySelector("[data-gl-target-selection]")?.value,
    coverage: document.querySelector("[data-gl-air-coverage]")?.value,
    contours: document.querySelector("[data-bathymetry-contours]")?.checked,
    bathymetry: document.querySelector("[data-bathymetry-raster]")?.checked
  });
}

function keepLinkCurrent(map) {
  let timer = null;
  const update = () => {
    clearTimeout(timer);
    timer = setTimeout(() => history.replaceState(null, "", currentViewHash(map)), 250);
  };
  map.on("moveend", update);
  document.addEventListener("change", (event) => {
    if (event.target.matches("[data-gl-layer], [data-gl-forecast], [data-gl-depth], [data-gl-target-temperature], [data-gl-target-selection], [data-gl-air-coverage], [data-bathymetry-contours], [data-bathymetry-raster]")) update();
  });
  document.addEventListener("great-lakes-history-time", update);
}

const SHARE_LAYER_SUBJECTS = {
  temperature: "water temperature",
  "air-temperature": "air temperature",
  "target-temperature": "target-temperature depths",
  wind: "wind forecast",
  thermocline: "thermocline depth",
  currents: "currents",
  waves: "wave conditions"
};
let shareFeedbackTimer = null;

function mapCenterLake(map) {
  const { lat, lng } = map.getCenter();
  const inModelBounds = (model) => {
    const [[south, west], [north, east]] = GREAT_LAKES_MODEL_BOUNDS[model];
    return lat >= south && lat <= north && lng >= west && lng <= east;
  };

  if (lat >= 46.5 && inModelBounds("LSOFS")) return "Lake Superior";
  if (lat < 43 && inModelBounds("LEOFS")) return "Lake Erie";
  // The LOOFS rectangle reaches west into Georgian Bay. Prefer LMHOFS anywhere
  // both model envelopes overlap so this Huron water is not mislabeled Ontario.
  if (inModelBounds("LMHOFS")) return lng < -84.75 ? "Lake Michigan" : "Lake Huron";
  if (lat >= 43 && inModelBounds("LOOFS")) return "Lake Ontario";
  return "";
}

function shareBlurb(map) {
  const layer = document.querySelector("[data-gl-layer]")?.value || "";
  const slider = document.querySelector("[data-gl-depth]");
  const depth = slider ? greatLakesDepthFromSlider(slider.value) : 0;
  let subject = SHARE_LAYER_SUBJECTS[layer] || "";
  if (subject && depth > 0.25 && (layer === "temperature" || layer === "currents")) {
    subject += ` at ${greatLakesDepthLabel(depth)}`;
  }

  const lake = mapCenterLake(map);
  if (!subject) return lake ? `Explore ${lake} on the Great Lakes map.` : "Explore the Great Lakes map.";
  return `Check out the ${subject} ${lake ? `on ${lake}` : "across the Great Lakes"}.`;
}

async function copyShareText(text) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch { /* Try the legacy copy command when clipboard permissions are unavailable. */ }
  }

  const field = document.createElement("textarea");
  field.value = text;
  field.setAttribute("readonly", "");
  field.style.position = "fixed";
  field.style.opacity = "0";
  document.body.append(field);
  let copied = false;
  try {
    field.select();
    copied = document.execCommand("copy");
  } finally {
    field.remove();
  }
  if (!copied) throw new Error("Clipboard copy failed");
}

async function shareView(map, button) {
  // Mark links made by this button so analytics can distinguish an opened
  // share from an ordinary bookmarked or internally updated map URL.
  const url = `${location.origin}${location.pathname}${location.search}${sharedViewHash(currentViewHash(map))}`;
  const detail = {
    lake: mapCenterLake(map),
    layer: document.querySelector("[data-gl-layer]")?.value || "",
  };
  try {
    await copyShareText(`${shareBlurb(map)}\n${url}`);
    document.dispatchEvent(new CustomEvent("great-lakes-share-copy", {
      detail,
    }));
    document.dispatchEvent(new CustomEvent("great-lakes-share-result", {
      detail: { ...detail, success: true }
    }));
    button.textContent = "Message and link copied";
  } catch {
    document.dispatchEvent(new CustomEvent("great-lakes-share-result", {
      detail: { ...detail, success: false }
    }));
    button.textContent = "Copy failed; try again";
  }
  clearTimeout(shareFeedbackTimer);
  shareFeedbackTimer = setTimeout(() => { button.textContent = "Share this view"; }, 2500);
}

// "My location": shows where you are (needs HTTPS outside localhost).
function addLocateControl(map) {
  let marker = null, accuracy = null;
  const Locate = L.Control.extend({
    options: { position: "bottomright" },
    onAdd() {
      const container = L.DomUtil.create("div", "leaflet-bar glc-tool glc-locate");
      const button = L.DomUtil.create("button", "", container);
      button.type = "button";
      button.title = "Locate me";
      button.setAttribute("aria-label", "Locate me");
      if (document.getElementById("map-location-note")) {
        button.setAttribute("aria-describedby", "map-location-note");
      }
      setHtml(button, html`<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="4"/><path d="M12 2v3m0 14v3M2 12h3m14 0h3"/><circle cx="12" cy="12" r="8" fill="none"/></svg>`);
      L.DomEvent.disableClickPropagation(container);
      L.DomEvent.on(button, "click", () => {
        button.classList.add("is-locating");
        document.dispatchEvent(new CustomEvent("great-lakes-map-interaction", {
          detail: { action: "locate_requested", zoom: map.getZoom(), lake: mapCenterLake(map) }
        }));
        map.locate({ setView: true, maxZoom: 11, enableHighAccuracy: true, timeout: 15000 });
      });
      map.on("locationfound", (event) => {
        button.classList.remove("is-locating");
        marker?.remove();
        accuracy?.remove();
        accuracy = L.circle(event.latlng, { radius: event.accuracy, interactive: false, color: "#1a78d2", weight: 1, fillOpacity: 0.12 }).addTo(map);
        marker = L.circleMarker(event.latlng, { radius: 7, color: "#fff", weight: 3, fillColor: "#1a78d2", fillOpacity: 1, interactive: false }).addTo(map);
        document.dispatchEvent(new CustomEvent("great-lakes-geolocation-found", {
          detail: {
            latitude: event.latlng.lat,
            longitude: event.latlng.lng,
            accuracyMeters: event.accuracy,
          },
        }));
        document.dispatchEvent(new CustomEvent("great-lakes-map-interaction", {
          detail: { action: "locate_succeeded", zoom: map.getZoom(), lake: mapCenterLake(map) }
        }));
      });
      map.on("locationerror", () => {
        button.classList.remove("is-locating");
        document.dispatchEvent(new CustomEvent("great-lakes-map-interaction", {
          detail: { action: "locate_failed", zoom: map.getZoom(), lake: mapCenterLake(map) }
        }));
        L.popup({ className: "gl-popup", maxWidth: 280 }).setLatLng(map.getCenter()).setContent("Your location is unavailable. Allow location access for this site (it needs HTTPS).").openOn(map);
      });
      return container;
    }
  });
  new Locate().addTo(map);
}

function init() {
  setupApi();
  setupConditions();
  setupPoints();
  const linked = parseViewHash(location.hash);
  const adminPortMap = Boolean(document.querySelector("[data-port-verification]"));
  if (adminPortMap && linked.layer === undefined) linked.layer = "";
  const tutorialWillShow = !adminPortMap && shouldShowMapTutorial();
  const forceTutorial = forceMapTutorial();
  const map = L.map("map", { zoomControl: false, zoomSnap: 1, worldCopyJump: true, minZoom: 4 });
  addMobileAttributionControl(map);
  L.control.zoom({ position: "bottomright" }).addTo(map);
  addLocateControl(map);
  addMeasureControl(map);
  L.control.scale({ position: "bottomleft", imperial: true, metric: true }).addTo(map);
  ui.map = map;
  setupMapProfileHint(tutorialWillShow);
  setBasemap(map, savedBasemap());
  setupPanel(map);
  setupBathymetryContours(map);
  setupBathymetryRaster(map);
  setupPortVerification(map);
  // The map is not placed yet, so the first layer waits for setView below.
  ensureGreatLakesConditions(map);
  applyLinkedControls(linked);
  let initialMapMove = true;
  map.on("moveend", () => {
    if (initialMapMove) {
      initialMapMove = false;
      return;
    }
    document.dispatchEvent(new CustomEvent("great-lakes-map-interaction", {
      detail: { action: "view_changed", zoom: map.getZoom(), lake: mapCenterLake(map) }
    }));
  });
  if (linked.center && !forceTutorial) map.setView(linked.center, linked.zoom);
  else if (tutorialWillShow) map.setView(MAP_TUTORIAL_CENTER, MAP_TUTORIAL_ZOOM);
  else if (linked.center) map.setView(linked.center, linked.zoom);
  else map.fitBounds(ALL_LAKES_BOUNDS);
  setupMapAnalytics(map, mapCenterLake);
  keepLinkCurrent(map);
  setupFirstVisitHint(map, tutorialWillShow);
  document.querySelector("[data-share]")?.addEventListener("click", (event) => shareView(map, event.currentTarget));
  // While measuring, clicks place measurement points instead of opening readings.
  map.on("click", (event) => {
    if (isMeasuring() || isPortVerificationEditing()) return;
    pauseGreatLakesAnimation();
    const latitude = Number(event.latlng?.lat);
    const longitude = Number(event.latlng?.lng);
    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      document.dispatchEvent(new CustomEvent("great-lakes-map-click", {
        detail: { latitude, longitude, lake: lakeNameFromCoordinates(latitude, longitude), zoom: map.getZoom() }
      }));
    }
    showPointPopup(map, event);
  });
  // A reading belongs to the layer and time it was taken for.
  document.addEventListener("change", (event) => {
    if (event.target.matches("[data-gl-layer], [data-gl-forecast], [data-gl-depth], [data-gl-target-temperature], [data-gl-target-selection], [data-gl-air-coverage]")) map.closePopup();
  });
  document.addEventListener("glc-units-changed", () => {
    const depthLabel = document.querySelector("[data-gl-depth-label]");
    const slider = document.querySelector("[data-gl-depth]");
    if (depthLabel && slider) depthLabel.textContent = greatLakesDepthLabel(greatLakesDepthFromSlider(slider.value));
    loadGreatLakesConditions(map);
  });
}

if (typeof document !== "undefined" && document.querySelector("#map")) init();
